import { Component, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { MerakisService } from '@app/features/merakis/shared/merakis.service';
import { LocationsService } from '@app/features/locations/shared/locations.service';
import { Location } from '@app/shared/models/location.model';
import { MerakiSwitchStatus } from '@app/shared/models/meraki/meraki-switch-status.model';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MerakiLocation } from '@app/shared/models/meraki/meraki-location.model';
import { firstValueFrom } from 'rxjs';

@Component({
    selector: 'app-chain-wide-merakis',
    templateUrl: './chain-wide-merakis.component.html',
    styleUrls: ['./chain-wide-merakis.component.css'],
    standalone: false
})
export class ChainWideMerakisComponent implements OnInit {
    @ViewChild('switchTable') adminTable: MatTable<MerakiSwitchStatus>;
    @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
    @ViewChild(MatSort, { static: true }) sort: MatSort;
    isLoading = false;
    onlyShowOffline: boolean;
    hideLabs: boolean;
    hideFutures: boolean;
    hideRemodels: boolean;
    filterText: string = null;
    all_switches = [];
    merakiLocations = [];
    locations = [];
    displayedColumns = ['storeNumber', 'locationName', 'locationTimeZone', 'locationStatus', 'merakis'];
    dataSource = new MatTableDataSource<MerakiLocation>(this.merakiLocations);

    constructor(private merakisService: MerakisService, private locationsService: LocationsService) { }

    ngOnInit() {
        this.initComponent();
    }

    private async initComponent() {
        this.isLoading = true;
        await this.createDataSource();
        this.dataSource.paginator = this.paginator;
        this.dataSource.sortingDataAccessor = (item, property) => {
            switch (property) {
                case 'storeNumber': return item.location.number;
                case 'locationName': return item.location.name;
                case 'locationTimeZone': return item.location.timezone;
                case 'locationStatus': return item.location.status;
                default: return item[property];
            }
        };
        this.dataSource.sort = this.sort;
        this.adminTable.renderRows();
        
        this.isLoading = false;
    }

    async getAllSwitches() {
        try {
            const observable = await this.merakisService.getAllSwitches();
            const merakis_from_response = await firstValueFrom(observable);
            if (typeof merakis_from_response === 'string') {
                console.log("Merakis timed out!")
                return;
            }
            merakis_from_response["message"].forEach(meraki_json => {
                let meraki: MerakiSwitchStatus = meraki_json;
                this.all_switches.push(meraki);
            });
        } catch (error) {
            console.log("Merakis timed out!")
        }
    }

    async getAllLocations() {
        try {
            const observable = await this.locationsService.getAllLocationData();
            const locations_from_response = await firstValueFrom(observable);
            if (typeof locations_from_response === 'string') {
                console.log("Locations timed out!");
                return;
            }
            locations_from_response["message"]["locationList"].forEach(locations_json => {
                let location: Location = new Location;
                location.name = locations_json["core"]["locationName"];
                location.number = locations_json["locationNumber"];
                location.status = locations_json["locationStatus"]["locationStatus"];

                if (!locations_json["core"].hasOwnProperty('timeZone')
                    || !locations_json["core"]["timeZone"].hasOwnProperty('timeZoneName')) {
                    location.timezone = "";
                } else {
                    location.timezone = locations_json["core"]["timeZone"]["timeZoneName"].split(" ")[2];
                }

                // Some locations don't have operators, such as locations with a 'future' state
                if (!locations_json.hasOwnProperty('operator')) {
                    location.operatorName = "";
                } else {
                    location.operatorName = locations_json["operator"]["name"];
                }
                this.locations.push(location);
            });
        } catch (error) {
            console.log(error);
        }
    }

    sortSwitches() {
        let sortedSwitches = new Map<string, MerakiSwitchStatus[]>();
        for (let meraki of this.all_switches as MerakiSwitchStatus[]) {
            let storeNumber = "";

            if (meraki.name != null) {
                storeNumber = meraki.name.substring(0,5);
            }
            
            if (sortedSwitches.has(storeNumber)) {
                sortedSwitches.get(storeNumber).push(meraki);
            } else {
                let switches = [];
                switches.push(meraki);
                sortedSwitches.set(storeNumber, switches);
            }
        }
        return sortedSwitches;
    }
    sortLocations() {
        let sortedLocations = new Map<string, Location>();
        this.locations.forEach(location => {
            sortedLocations.set(location.number, location);
        });
        return sortedLocations;
    }

    async createDataSource(locations?) {

        if (locations == null) {
            await this.getAllLocations();
        } else {
            this.locations = locations;
        }

        await this.getAllSwitches();

        let sortedSwitches = this.sortSwitches();
        let sortedLocation = this.sortLocations();

        sortedSwitches.forEach((value: MerakiSwitchStatus[], key: string) => {
            let merakiLocation: MerakiLocation = new MerakiLocation;
            merakiLocation.merakis = value;

            // Labs use the 9xxxx store-number range or a "Lab"-prefixed switch name.
            // They aren't present in the location data, so derive this from the
            // switch-name key instead of location.number.
            merakiLocation.isLab = key.charAt(0) == "9" || key.toLowerCase().startsWith("lab");

            if (sortedLocation.has(key)) {
                merakiLocation.location = sortedLocation.get(key);
            } else {
                // Switch name did not match a real store (labs, decommissioned, and
                // test gear). Surface the switch-name key as the store number so the
                // row sorts after real stores instead of floating to the top with a
                // blank store number.
                let location: Location = new Location;
                location.name = "";
                location.number = key;
                location.operatorName = "";
                location.status = "";
                location.timezone = "";
                merakiLocation.location = location;
            }
            this.merakiLocations.push(merakiLocation);
        });
    }

    onToggle() {
        this.applyFilter(this.filterText);
    }

    applyFilter(filterValue: string) {
        this.dataSource.filterPredicate = (data: MerakiLocation, filter: string) => {
            let hasOfflineMerakis = false;
            let containsFilterValue = false;

            // Handle offline switches
            if (this.onlyShowOffline) {
                for (let meraki of data.merakis) {
                    if (meraki.status == "offline") {
                        hasOfflineMerakis = true;
                    }
                }
            }

            // Handle filter text filtering
            if (filter == "null"
                || data.location.number.toLowerCase().includes(filterValue)
                || data.location.name.toLowerCase().includes(filterValue)
                || data.location.timezone.toLowerCase().includes(filterValue)
                || data.location.status.toLowerCase().includes(filterValue)) {
                containsFilterValue = true;
            }

            // Coalesce and make filtering decision
            if (this.hideLabs && data.isLab) {
                return false;
            } else if (this.hideFutures && data.location.status == "future") {
                return false;
            } else if (this.hideRemodels && data.location.status == "remodel") {
                return false;
            } else if (!containsFilterValue) {
                return false
            } else if (this.onlyShowOffline && !hasOfflineMerakis) {
                return false;
            } else {
                return true;
            }
        }

        if (filterValue == null || filterValue == "") { // handles case where no filter text is provided
            this.dataSource.filter = "null";
        } else {
            this.dataSource.filter = filterValue.trim().toLowerCase();
        }
    }

}